import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { onSnapshot } from 'firebase/firestore';
import { auth } from '../lib/firebase';
import { userDoc } from '../lib/firestore';
import type { AppUser } from '../types';

interface AuthContextValue {
  firebaseUser: User | null;
  appUser: AppUser | null;
  loading: boolean;
  displacedByOtherSession: false;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubUser: (() => void) | undefined;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      unsubUser?.();
      unsubUser = undefined;

      setFirebaseUser(user);

      if (!user) {
        setAppUser(null);
        setLoading(false);
        return;
      }

      // Keep loading=true until the Firestore doc arrives (covers fresh sign-in)
      setLoading(true);

      // Force-refresh the ID token so custom claims (schoolId, role) set by
      // createUser/provisionSchool are guaranteed to be present in the JWT
      // before any Firestore security rule evaluation happens.
      user.getIdToken(true).catch(() => {/* non-fatal */}).finally(() => {
        unsubUser = onSnapshot(userDoc(user.uid), (snap) => {
          if (!snap.exists()) {
            setAppUser(null);
            setLoading(false);
            return;
          }

          const data = snap.data() as unknown as Record<string, unknown>;
          setAppUser({ ...data, id: snap.id } as AppUser);
          setLoading(false);
        });
      });
    });

    return () => {
      unsubAuth();
      unsubUser?.();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signOut = () => firebaseSignOut(auth);

  return (
    <AuthContext.Provider value={{ firebaseUser, appUser, loading, displacedByOtherSession: false, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
